import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowRight, Linkedin, Instagram, Twitter, Facebook, Globe, Calendar, Mic, Star, Play, MapPin, Ticket } from 'lucide-react'
import CinematicNav, { BackToTop } from '../../components/landing/CinematicNav'
import CinematicFooter from '../../components/landing/CinematicFooter'
import Reveal from '../../components/landing/Reveal'
import Breadcrumbs from '../../components/masterclass/Breadcrumbs'
import SpeakerSchema from '../../components/SEO/SpeakerSchema'
import ShareEvent from '../../components/masterclass/ShareEvent'
import SpeakerImage from '../../components/masterclass/SpeakerImage'
import { resolveImageUrl } from '../../utils/imageUrl'
import { SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'
import LoadingSpinner from '../../components/LoadingSpinner'

function formatDate(d) {
  return d && !isNaN(new Date(d).getTime()) ? new Date(d).toLocaleDateString('en-UG', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
}

export default function SpeakerDetailPage() {
  const { id } = useParams()
  const [speaker, setSpeaker] = useState(null)
  const [loading, setLoading] = useState(true)
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setLoading(true)
    masterclassService.getSpeaker(id)
      .then((res) => {
        setSpeaker(res.speaker)
        document.title = `${res.speaker.name} | ${SCHEDULE_SERIES_NAME} Speakers`
      })
      .catch(() => {
        document.title = `Speaker Not Found | ${SCHEDULE_SERIES_NAME}`
      })
      .finally(() => setLoading(false))
  }, [id])

  const [upcoming, setUpcoming] = useState(null)
  useEffect(() => {
    masterclassService.getUpcoming()
      .then((r) => setUpcoming(r.masterclass))
      .catch(() => {})
  }, [])

  if (loading) return <LoadingSpinner className="cinema-page min-h-screen" />

  if (!speaker) {
    return (
      <div className="cinema-page min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="cinema-headline-lg text-white">Speaker not found</h1>
          <Link to="/masterclass/speakers" className="cinema-btn-text mt-6 inline-flex">Browse speakers</Link>
        </div>
      </div>
    )
  }

  const social = (() => {
    if (Array.isArray(speaker.social_links)) return speaker.social_links
    if (typeof speaker.social_links === 'string' && speaker.social_links.trim()) {
      try { return JSON.parse(speaker.social_links) } catch { return [] }
    }
    return []
  })()
  const sessions = speaker.sessions || []
  const gallery = speaker.gallery || []
  const videos = speaker.videos || []
  const testimonials = speaker.testimonials || []

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <SpeakerSchema />
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 right-0 opacity-30" />
        <div className="cinema-container relative">
          <Reveal>
            <Breadcrumbs title={speaker.name} />
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-16 items-center mt-10">
            <Reveal delay={100}>
              {(() => {
                const hasPhoto = resolveImageUrl(speaker?.photo_url || speaker?.photo)
                return hasPhoto && (
                  <div className="relative max-w-md mx-auto lg:mx-0">
                    <div className="aspect-[3/4] overflow-hidden">
                      <SpeakerImage speaker={speaker} loading="lazy" fit="card" />
                    </div>
                    <div className="absolute -bottom-4 -right-4 bg-cinema-glow text-white px-6 py-3 font-sans text-xs uppercase tracking-widest font-bold">
                      Industry Speaker
                    </div>
                  </div>
                )
              })()}
            </Reveal>
            <Reveal delay={200}>
              <span className="cinema-label">Speaker Profile</span>
              <h1 className="cinema-headline-lg mt-6 text-white">{speaker.name}</h1>
              <p className="text-cinema-gold font-sans text-xl mt-3">{speaker.title}</p>
              {speaker.company && <p className="text-cinema-muted font-sans text-lg mt-1">{speaker.company}</p>}
              {speaker.specialization && (
                <p className="text-cinema-soft font-sans text-sm mt-4 uppercase tracking-wider">
                  {speaker.specialization}
                </p>
              )}
              {speaker.experience && (
                <div className="mt-6 inline-flex items-center gap-3 text-cinema-gold">
                  <Star className="w-5 h-5" />
                  <span className="text-white font-sans text-sm">{speaker.experience}</span>
                </div>
              )}
              {speaker.bio && <p className="cinema-body mt-8 max-w-xl">{speaker.bio}</p>}
              <div className="mt-6">
                <ShareEvent url={`${window.location.origin}/speakers/${speaker.slug || speaker.id}`} title={speaker.name} summary={speaker.bio} />
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                {speaker.linkedin_url && (
                  <a href={speaker.linkedin_url} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Linkedin className="w-4 h-4" /></a>
                )}
                {speaker.instagram_url && (
                  <a href={speaker.instagram_url} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Instagram className="w-4 h-4" /></a>
                )}
                {speaker.twitter_url && (
                  <a href={speaker.twitter_url} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Twitter className="w-4 h-4" /></a>
                )}
                {speaker.facebook_url && (
                  <a href={speaker.facebook_url} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Facebook className="w-4 h-4" /></a>
                )}
                {speaker.website_url && (
                  <a href={speaker.website_url} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Globe className="w-4 h-4" /></a>
                )}
                {social.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors">
                    <Globe className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Masterclasses Delivered */}
      <section className="bg-cinema-charcoal py-24 sm:py-32">
        <div className="cinema-container">
          <Reveal>
            <span className="cinema-label">Sessions</span>
            <h2 className="cinema-headline-lg mt-6 text-white">Masterclasses Delivered</h2>
            <div className="cinema-divider mt-10 mb-16" />
          </Reveal>
          {sessions.length === 0 ? (
            <p className="text-cinema-muted font-sans">No sessions recorded yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessions.map((session, i) => (
                <Reveal key={session.id} delay={i * 60}>
                  <Link to={`/masterclass/${session.slug || session.id}`} className="block h-full border-b border-white/5 pb-6 group">
                    <div className="space-y-4">
                      <span className="cinema-label text-xs">{session.status || 'Completed'}</span>
                      <h3 className="font-display text-xl text-white font-medium leading-tight group-hover:text-cinema-gold transition-colors">
                        {session.topic}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-cinema-muted font-sans text-xs">
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-cinema-gold" /> {formatDate(session.event_date)}</span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-cinema-gold" /> {session.venue || 'SMA'}</span>
                      </div>
                      <div className="pt-2 flex items-center gap-2 text-cinema-gold text-sm font-medium">
                        View Recap <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="bg-cinema-black py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Gallery</span>
              <h2 className="cinema-headline-lg mt-6 text-white">Speaker Gallery</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {gallery.slice(0, 8).map((img, i) => (
                <Reveal key={img.id} delay={i * 50}>
                  <div className="aspect-square overflow-hidden group">
                    <img src={resolveImageUrl(img.image_url)} alt={img.caption || ''} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <section className="bg-cinema-charcoal py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Videos</span>
              <h2 className="cinema-headline-lg mt-6 text-white">Speaker Videos</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 60}>
                  <div className="overflow-hidden group">
                    <div className="aspect-video relative overflow-hidden">
                      <img src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`} alt={v.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-10 h-10 text-white fill-white" />
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-white font-sans text-sm font-medium">{v.title}</p>
                      <p className="text-cinema-muted text-xs mt-1">{v.category || 'Highlight'}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-cinema-black py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Testimonials</span>
              <h2 className="cinema-headline-lg mt-6 text-white">Student Reviews</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 60}>
                  <div className="p-6 h-full flex flex-col border-b border-white/5">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, idx) => (
                        <Star key={idx} className={`w-4 h-4 ${idx < t.rating ? 'text-cinema-gold fill-cinema-gold' : 'text-cinema-muted'}`} />
                      ))}
                    </div>
                    <p className="text-cinema-soft font-sans text-sm leading-relaxed flex-1">"{t.review}"</p>
                    <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/5">
                      <img src={resolveImageUrl(t.student_photo_url) || '/Tutors/default.jpg'} alt={t.student_name} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="text-white font-sans text-sm font-medium">{t.student_name}</p>
                        <p className="text-cinema-muted text-xs">{t.course || 'Student'}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />

      {/* Floating sticky CTA — appears after scrolling past hero */}
      {upcoming && (
        <div
          className={`fixed bottom-0 inset-x-0 z-40 transition-all duration-500 ${
            scrolled ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-full opacity-0 pointer-events-none'
          }`}
        >
          <div className="bg-cinema-black/95 backdrop-blur-md border-t border-white/8 py-3 px-4 sm:px-6">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Ticket className="w-4 h-4 text-cinema-gold shrink-0" />
                <p className="font-sans text-white text-sm truncate">
                  <span className="text-cinema-gold font-medium">Register</span> to hear{' '}
                  <span className="font-medium text-white">{speaker.name}</span>{' '}
                  <span className="text-cinema-muted hidden sm:inline">and this month's masterclass lineup — live at SMA</span>
                </p>
              </div>
              <Link
                to="/masterclass"
                className="cinema-btn-primary !py-2 !px-5 !text-xs shrink-0 group whitespace-nowrap"
              >
                Get My Pass
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
